import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Block,
	Input,
	Label,
	Link,
	Modal,
	ModalContent,
	ModalControls,
	ModalHeader,
	Button,
	Toggle,
	Title,
	Tooltip
} from '@upsales/components';
import { PrimaryButton } from '@upsales/components/Buttons';
import { SelectItem } from '@upsales/components/Utils/selectHelpers';
import BemClass from '@upsales/components/Utils/bemClass';
import T from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import ContactResource from 'App/resources/Contact';
import ClientResource from 'App/resources/Client';
import ProjectResource, { Project } from 'App/resources/Project';
import ComparisonTypes from 'Resources/ComparisonTypes';
import type Contact from 'App/resources/Model/Contact';
import type Client from 'App/resources/Model/Client';
import FormObserver, { FormObserverOnFieldChange, mapCustomValuesToArray } from '../FormObserver/FormObserver';
import PhoneInput from 'Components/Inputs/PhoneInput';
import StandardFieldInput from 'Components/Inputs/standardFieldInput';
import type { StandardFieldConfig } from 'App/resources/AllIWant';
import NotesWithSignature from 'Components/Inputs/NotesWithSignature/NotesWithSignature';
import CustomFields from '../CustomFields/CustomFields';
import LabeledInput from '../Inputs/LabeledInput';
import { useAppCategories } from '../Inputs/Selects/StaticCategorySelect/StaticCategorySelect';
import { StaticJourneyStepSelect } from '../Inputs/Selects/StaticJourneyStepSelect/StaticJourneyStepSelect';
import { StaticMultiSelect } from '../Inputs/Selects/StaticSelect';
import type { CoreSelectItem } from '../Inputs/Selects/StaticSelect';
import type Category from 'App/resources/Model/Category';
import type JourneyStep from 'App/resources/Model/JourneyStep';
import { ResourceMultiSelect, ResourceSelect } from '../ResourceSelect';
import FieldTranslations from 'Resources/FieldTranslations';
import { useCustomFields, useJourneySteps, useMetadata } from '../hooks/appHooks';
import { useFeatureAvailable, useSoftDeployAccess, useSelector } from '../hooks';
import { Feature } from '../hooks/featureHelper';
import getEditContactValidationModel from './getEditContactValidationModel';
import OptinsSelect from './OptinsSelect/OptinsSelect';
import { TitleTypeahead } from './TitleTypeAhead/TitleTypeAhead';
import EmailField from './EmailField';
import useEditContactEmail from './useEditContactEmail';
import {
	EditContactProps,
	FieldTranslationItem,
	ContactOptin,
	MappedContact,
	addSalutationFilters,
	addTitleCategoryFilters,
	buildInitialValues,
	getFieldTranslationItem
} from './editContactHelpers';
import './EditContact.scss';
import AppService from 'App/legacy/AppService';

export { openEditContact } from './openEditContact';
export type { MappedContact } from './editContactHelpers';

const EditContact = ({
	className,
	close,
	id,
	contact,
	clientId,
	account,
	forceRequiredEmail,
	titleCategory: titleCategoryInput
}: EditContactProps) => {
	const classes = new BemClass('EditContact', className);
	const modalRef = useRef<HTMLDivElement>(null);
	const metadata = useMetadata();
	const customFields = useCustomFields('contact');
	const journeySteps = useJourneySteps();
	const hasJourney = useFeatureAvailable(Feature.JOURNEY_STATUS);
	const hasOptIn = useFeatureAvailable(Feature.OPT_IN);
	const hasNewFields = useSoftDeployAccess('NEW_FIELDS');
	const allCategories = useAppCategories('contact');
	const categoryTypes = useSelector(({ App }) => App.categoryTypes?.contact ?? []) as { id: number; name: string }[];
	const categoryTypeOf = (catId: number) =>
		((allCategories.find(c => c.id === catId) as { categoryType?: number } | undefined)?.categoryType ??
			0) as number;
	const categoryOptionsFor = (typeId: number) =>
		allCategories.filter(c => ((c as { categoryType?: number }).categoryType ?? 0) === typeId);

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [initialValues, setInitialValues] = useState<MappedContact | null>(null);
	const [salutation, setSalutation] = useState<FieldTranslationItem | null>(null);
	const [titleCategory, setTitleCategory] = useState<FieldTranslationItem | null>(null);
	const [manualOptins, setManualOptins] = useState<CoreSelectItem[]>([]);

	const isEdit = !!id || !!contact?.id;
	const showAccount = !isEdit && !account && !clientId;
	const standardFields = metadata?.standardFields?.Contact ?? {};
	const showSalutation = hasNewFields && !!standardFields.Salutation?.active;
	const showTitleCategory = hasNewFields && !!standardFields.TitleCategory?.active;
	const socialFields = useMemo(
		() =>
			(Object.values(standardFields) as StandardFieldConfig[])
				.filter(field => field.group === 'social' && field.active)
				.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
		[metadata]
	);

	const email = useEditContactEmail({ isEdit, close });

	const requiredFields = useMemo(() => {
		const fields = { ...(metadata?.requiredFields?.Contact ?? {}) } as { [key: string]: boolean };
		if (forceRequiredEmail) {
			fields.Email = true;
		}
		return fields;
	}, [metadata, forceRequiredEmail]);

	const validationModel = useMemo(
		() => getEditContactValidationModel({ requiredFields, standardFields, hasNewFields, customFields }),
		[requiredFields, standardFields, hasNewFields, customFields]
	);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			try {
				let loaded: Partial<Contact>;
				if (id) {
					loaded = (await ContactResource.get(id)).data;
				} else if (contact) {
					loaded = contact;
				} else {
					loaded = ContactResource.new() as Partial<Contact>;
					loaded.active = 1;
					if (account) {
						loaded.client = account as Contact['client'];
					} else if (clientId) {
						loaded.client = (await ClientResource.get(clientId)).data;
					}
					if (titleCategoryInput) {
						(loaded as { titleCategory?: FieldTranslationItem }).titleCategory = titleCategoryInput;
					}
					if (metadata?.params.SyncJourneyStatus && loaded.client) {
						loaded.journeyStep = loaded.client.journeyStep;
					}
				}

				if (cancelled) {
					return;
				}

				// Run contact_edit user scripts (parity with the Angular modal). Scripts
				// may mutate the contact, so run before the form snapshot. A failing
				// script must not block the modal from opening.
				try {
					await Tools.ScriptService.contact.edit(loaded);
				} catch (e) {
					logError(e as Error, 'contact_edit script failed');
				}

				if (cancelled) {
					return;
				}

				const socialSeed: Record<string, unknown> = {};
				socialFields.forEach(field => {
					socialSeed[field.field] = (loaded as Record<string, unknown>)[field.field] ?? '';
				});
				setInitialValues({ ...buildInitialValues(loaded, customFields), ...socialSeed } as MappedContact);
				setManualOptins((loaded.optins ?? []).filter(o => o.type === 'manual'));

				if (loaded.salutation?.tagId) {
					getFieldTranslationItem(loaded.salutation.tagId, addSalutationFilters)
						.then(item => {
							if (!cancelled) setSalutation(item);
						})
						.catch(e => logError(e as Error, 'Failed to set salutation'));
				}
				const loadedTitleCategory = (loaded as { titleCategory?: FieldTranslationItem }).titleCategory;
				if (loadedTitleCategory?.tagId) {
					getFieldTranslationItem(loadedTitleCategory.tagId, addTitleCategoryFilters)
						.then(item => {
							if (!cancelled) setTitleCategory(item);
						})
						.catch(e => logError(e as Error, 'Failed to set title category'));
				}

				setLoading(false);
				email.initEmailState(loaded);
			} catch (e) {
				logError(e as Error, 'Failed to open contact');
				if (!cancelled) {
					close(undefined);
				}
			}
		};
		load();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const addAccount = (onFormChange: FormObserverOnFieldChange) => {
		Tools.$upModal
			.open('editAccount', { customerId: AppService.getCustomerId(), fromModal: true })
			.then((res: Client) => {
				if (res) {
					onFormChange('client', { id: res.id, name: res.name });
				}
			})
			.catch(e => logError(e as Error, 'Failed to open account modal'));
	};

	const onToggleActive = (value: boolean, onFormChange: FormObserverOnFieldChange) => {
		const active = value ? 1 : 0;
		onFormChange('active', active);
		if (id) {
			setSaving(true);
			ContactResource.save({ id, active } as Partial<Contact>)
				.catch(e => {
					logError(e as Error, 'Failed to toggle active');
					onFormChange('active', value ? 0 : 1);
				})
				.finally(() => setSaving(false));
		}
	};

	const removeContact = async () => {
		if (!id) {
			return;
		}
		try {
			await ContactResource.delete(id);
			close(undefined);
		} catch (e) {
			logError(e as Error, 'Failed to delete contact');
		}
	};

	const save = async (values: MappedContact) => {
		setSubmitted(true);
		setSaving(true);
		const optins = manualOptins
			.concat((values.optins ?? []).filter(o => o.type !== 'manual') as unknown as CoreSelectItem[])
			.map(o => o as unknown as ContactOptin);
		// The API takes `name` and splits it into first/last itself; `firstName`
		// and `lastName` are read-only on the way out and are dropped by the
		// mapper on the way in (they are not in ContactAttributes). Sending only
		// those two — which is what this form's own fields produce — saved a
		// contact with no name at all, on create and on rename alike.
		const name = [values.firstName, values.lastName]
			.map(part => (part ?? '').trim())
			.filter(Boolean)
			.join(' ');

		const toSave = {
			...values,
			...(name ? { name } : {}),
			custom: mapCustomValuesToArray(values.custom),
			campaigns: values.projects,
			optins,
			mailBounces: email.mailBounces
		} as unknown as Partial<Contact>;
		try {
			await Tools.ScriptService.contact.save(toSave);
			const { data } = await ContactResource.save(toSave);
			close(data);
		} catch (e) {
			logError(e as Error, 'Failed to save contact');
			setSaving(false);
		}
	};

	if (loading || !initialValues) {
		return (
			<Modal className={`${classes.b()} edit-contact-modal`} size="lg">
				<ModalHeader title={T('default.contact')} icon="user" onClose={() => close(undefined)} />
				<ModalContent>
					<Block space="ptxl pbxl">
						<Title>{`${T('default.loading')}…`}</Title>
					</Block>
				</ModalContent>
			</Modal>
		);
	}

	return (
		<Modal className={`${classes.b()} edit-contact-modal`} size="lg">
			<div ref={modalRef}>
				<FormObserver<MappedContact>
					onSubmit={save}
					model={validationModel}
					initialValues={initialValues}
					validateOnMount
					useDebounce
				>
					{({ onFormChange, values, inputProps, submit }) => {
						const onChange = onFormChange as FormObserverOnFieldChange;
						const fieldInputProps = inputProps as Record<string, { name?: string; state?: 'error' }>;
						const fieldValues = values as Record<string, unknown>;
						const blockingMessage = email.getBlockingDuplicateMessage(values.email);
						const title =
							`${T(isEdit ? 'default.edit' : 'default.create')} ${T('default.contact').toLowerCase()}` +
							(!showAccount && values.client
								? ` ${T('default.at').toLowerCase()} ${values.client.name}`
								: '');
						return (
							<>
								<ModalHeader
									title={title}
									icon={isEdit ? 'edit' : 'user'}
									onClose={() => close(undefined)}
								/>
								<ModalContent>
									{showAccount ? (
										<Block space="mbxl">
											<LabeledInput
												title={T('default.account')}
												topRight={
													<Link onClick={() => addAccount(onChange)}>
														{T('default.addAccount')}
													</Link>
												}
												required
											>
												<ResourceSelect<'name'>
													resource={
														ClientResource as unknown as Parameters<
															typeof ResourceSelect
														>[0]['resource']
													}
													value={
														values.client
															? { id: values.client.id, title: values.client.name }
															: null
													}
													onChange={(item: SelectItem) => {
														const client = item
															? { id: Number(item.id), name: item.title }
															: null;
														onChange('client', client);
														email.validateEmail(client, values.id, values.email);
													}}
												/>
											</LabeledInput>
										</Block>
									) : null}

									<div className={classes.elem('grid').b()}>
										<div className={classes.elem('cell').b()}>
											<Label required={inputProps.firstName.required}>
												{T('default.firstName')}
											</Label>
											<Input
												{...inputProps.firstName}
												onChange={e => {
													onChange('firstName', e.target.value);
													email.getSuggestion(
														values.client,
														e.target.value,
														values.lastName,
														values.email
													);
												}}
											/>
										</div>
										<div className={classes.elem('cell').b()}>
											<Label required={inputProps.lastName.required}>
												{T('default.lastName')}
											</Label>
											<Input
												{...inputProps.lastName}
												onChange={e => {
													onChange('lastName', e.target.value);
													email.getSuggestion(
														values.client,
														values.firstName,
														e.target.value,
														values.email
													);
												}}
											/>
										</div>
										{showSalutation ? (
											<div className={classes.elem('cell').b()}>
												<Label required={inputProps.salutation?.required}>
													{T('default.salutation')}
												</Label>
												<ResourceSelect
													resource={FieldTranslations}
													titleField="value"
													value={salutation}
													modifyRb={addSalutationFilters}
													anchor={modalRef.current}
													onChange={(value: SelectItem | null) => {
														setSalutation(value as FieldTranslationItem | null);
														onChange('salutation', value);
													}}
												/>
											</div>
										) : null}
										{showTitleCategory ? (
											<div className={classes.elem('cell').b()}>
												<Label required={inputProps.titleCategory?.required}>
													{T('default.titlecategory')}
												</Label>
												<ResourceSelect
													resource={FieldTranslations}
													titleField="value"
													value={titleCategory}
													modifyRb={addTitleCategoryFilters}
													anchor={modalRef.current}
													onChange={(value: SelectItem | null) => {
														setTitleCategory(value as FieldTranslationItem | null);
														onChange('titleCategory', value);
													}}
												/>
											</div>
										) : null}
										<div className={classes.elem('cell').b()}>
											<Label required={inputProps.title.required}>{T('default.title')}</Label>
											<TitleTypeahead
												name="title"
												icon="user"
												value={values.title || ''}
												state={inputProps.title.state}
												required={inputProps.title.required}
												onChange={value => onChange('title', value)}
											/>
										</div>
										<div className={classes.elem('cell').b()}>
											<Label>{T('default.active')}</Label>
											<div>
												<Toggle
													checked={!!values.active}
													onChange={checked => onToggleActive(checked, onChange)}
												/>
											</div>
										</div>
										<div className={classes.elem('cell').b()}>
											<Label>{T('default.priority')}</Label>
											<div>
												<Toggle
													checked={!!values.isPriority}
													onChange={checked => onChange('isPriority', checked ? 1 : 0)}
												/>
											</div>
										</div>
										<div className={classes.elem('cell').b()}>
											<Label required={requiredFields.Phone}>{T('default.phone')}</Label>
											<PhoneInput
												name="phone"
												state={
													submitted && requiredFields.Phone && !values.phone
														? 'error'
														: undefined
												}
												phone={values.phone ?? ''}
												countryCode={values.phoneCountryCode}
												required={requiredFields.Phone}
												icon="phone"
												isNew={!isEdit}
												onChange={(number: string, countryCode: string) => {
													onChange('phoneCountryCode', countryCode);
													onChange('phone', number);
												}}
											/>
										</div>
										<div className={classes.elem('cell').b()}>
											<Label required={requiredFields.Cellphone}>{T('default.cellPhone')}</Label>
											<PhoneInput
												name="cellPhone"
												state={
													submitted && requiredFields.Cellphone && !values.cellPhone
														? 'error'
														: undefined
												}
												phone={values.cellPhone ?? ''}
												countryCode={values.cellPhoneCountryCode}
												required={requiredFields.Cellphone}
												icon="mobile"
												isNew={!isEdit}
												onChange={(number: string, countryCode: string) => {
													onChange('cellPhoneCountryCode', countryCode);
													onChange('cellPhone', number);
												}}
											/>
										</div>
										<EmailField
											email={email}
											value={values.email}
											client={values.client}
											contactId={values.id}
											required={requiredFields.Email}
											submitted={submitted}
											onChange={onChange}
										/>
										{categoryOptionsFor(0).length ? (
											<div className={classes.elem('cell').b()}>
												<Label>{T('default.categories')}</Label>
												<StaticMultiSelect
													options={
														categoryOptionsFor(0) as unknown as CoreSelectItem<Category>[]
													}
													value={
														values.categories
															.filter(c => categoryTypeOf(c.id) === 0)
															.map(c => ({
																...c,
																title: c.name
															})) as unknown as CoreSelectItem<Category>[]
													}
													anchor={modalRef.current}
													onChange={items => {
														const others = values.categories.filter(
															c => categoryTypeOf(c.id) !== 0
														);
														onChange('categories', [
															...others,
															...items
														] as MappedContact['categories']);
													}}
												/>
											</div>
										) : null}
										<div className={classes.elem('cell').b()}>
											<Label>{T('default.campaigns')}</Label>
											<ResourceMultiSelect
												resource={ProjectResource}
												value={values.projects as unknown as CoreSelectItem<Project>[]}
												anchor={modalRef.current}
												onChange={projects => onChange('projects', projects)}
												modifyRb={rb =>
													rb.addFilter(
														ProjectResource.attr.active,
														ComparisonTypes.Equals,
														true
													)
												}
											/>
										</div>
										{hasOptIn ? (
											<div className={classes.elem('cell').b()}>
												<Label>{T('default.addOptIn')}</Label>
												<OptinsSelect
													value={manualOptins}
													optinType="manual"
													anchor={modalRef.current}
													onChange={value => {
														setManualOptins(value);
														onChange('optins', value);
													}}
												/>
											</div>
										) : null}
										{categoryTypes.map(categoryType =>
											categoryOptionsFor(categoryType.id).length ? (
												<div className={classes.elem('cell').b()} key={categoryType.id}>
													<Label>{categoryType.name}</Label>
													<StaticMultiSelect
														options={
															categoryOptionsFor(
																categoryType.id
															) as unknown as CoreSelectItem<Category>[]
														}
														value={
															values.categories
																.filter(c => categoryTypeOf(c.id) === categoryType.id)
																.map(c => ({
																	...c,
																	title: c.name
																})) as unknown as CoreSelectItem<Category>[]
														}
														anchor={modalRef.current}
														onChange={items => {
															const others = values.categories.filter(
																c => categoryTypeOf(c.id) !== categoryType.id
															);
															onChange('categories', [
																...others,
																...items
															] as MappedContact['categories']);
														}}
													/>
												</div>
											) : null
										)}
										{hasJourney ? (
											<div className={classes.elem('cell').b()}>
												<Label>{T('default.journeyStep')}</Label>
												<StaticJourneyStepSelect
													value={(() => {
														const step = journeySteps.find(
															j => String(j.value) === String(values.journeyStep)
														);
														return step
															? ({
																	...step,
																	id: step.value,
																	title: T(step.name)
															  } as unknown as CoreSelectItem<JourneyStep>)
															: null;
													})()}
													anchor={modalRef.current}
													onChange={(value: CoreSelectItem<JourneyStep> | null) =>
														onChange('journeyStep', value ? String(value.id) : '')
													}
												/>
											</div>
										) : null}
									</div>

									{hasNewFields && socialFields.length ? (
										<>
											<Title space="mbm mtl">{T('visitor.type.social')}</Title>
											<div className={classes.elem('grid').b()}>
												{socialFields.map(field => (
													<div className={classes.elem('cell').b()} key={field.field}>
														<Label required={field.required}>{T(field.nameTag)}</Label>
														<StandardFieldInput
															name={fieldInputProps[field.field]?.name}
															state={fieldInputProps[field.field]?.state}
															config={field}
															value={String(fieldValues[field.field] ?? '')}
															onChange={(val: string) => onChange(field.field, val)}
														/>
													</div>
												))}
											</div>
										</>
									) : null}

									{customFields.length ? (
										<Block space="mbxl">
											<CustomFields
												type="contact"
												inputProps={inputProps}
												clientId={values.client?.id}
												showFormGroupName
												onChange={(fieldId: number, value: string) =>
													onChange(`custom.Custom_${fieldId}`, value)
												}
											/>
										</Block>
									) : null}

									<Block space="mbxl">
										<NotesWithSignature
											value={values.notes}
											onChange={(val: string) => onChange('notes', val)}
											required={inputProps.notes.required}
										/>
									</Block>
								</ModalContent>
								<ModalControls>
									<Tooltip disabled={!blockingMessage} position="top" title={blockingMessage ?? ''}>
										<PrimaryButton
											submit
											loading={saving}
											disabled={!!blockingMessage}
											onClick={submit}
										>
											{T('default.save')}
										</PrimaryButton>
									</Tooltip>
									<Button type="link" color="grey" onClick={() => close(undefined)}>
										{T('default.abort')}
									</Button>
									{isEdit && initialValues.userRemovable !== false ? (
										<Button type="link" color="red" onClick={removeContact}>
											{T('default.delete')}
										</Button>
									) : null}
								</ModalControls>
							</>
						);
					}}
				</FormObserver>
			</div>
		</Modal>
	);
};

export default EditContact;
